import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAbfCopilotContext, ExplainMetricButton } from '../copilot/AbfCopilotLayout';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Database,
  FileSearch,
  GitCompareArrows,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import {
  apiErrorMessage,
  compareReleasedCollateralTapes,
  getCollateralAssetDetail,
  getCollateralExplorer,
  reconcileReleasedCollateralMovements,
} from '../api/abfApi';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { plural } from '../presentation/format';
import type {
  CollateralAsset,
  CollateralAssetDetail,
  CollateralDimension,
  CollateralExplorerReadModel,
  CollateralSort,
  CollateralView,
  ReleasedTapeComparison,
} from '../types/collateralExplorer';
import IngestionWorkspace from '../ingestion/IngestionWorkspace';
import RuleWorkbench from './RuleWorkbench';
import { ExplorerSurface, LoanDialog } from './collateralWorkspaceStyles';

interface Props {
  facilityId: number;
  initialTapeId?: number;
  embedded?: boolean;
  servicerName?: string | null;
  userRole?: string;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const panel: React.CSSProperties = {
  border: '1px solid rgba(148,163,184,.16)',
  borderRadius: 14,
  background: 'rgba(8,16,30,.78)',
  boxShadow: '0 12px 36px rgba(2,6,23,.2)',
};
const button: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.4rem',
  minHeight: 40,
  padding: '.45rem .7rem',
  border: '1px solid rgba(148,163,184,.22)',
  borderRadius: 9,
  background: 'rgba(15,23,42,.72)',
  color: '#dbeafe',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 800,
};
const label: React.CSSProperties = {
  color: '#94a8c2',
  fontSize: '12px',
  fontWeight: 650,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
};
const value: React.CSSProperties = {
  color: '#f8fafc',
  fontSize: '14px',
  fontWeight: 800,
};

const money = (amount: unknown) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
    : '—';
const number = (amount: unknown) =>
  typeof amount === 'number' ? new Intl.NumberFormat('en-US').format(amount) : '—';
const display = (item: unknown) => {
  if (item === null || item === undefined || item === '') return 'Not supplied';
  if (typeof item === 'boolean') return item ? 'Yes' : 'No';
  return String(item).replace(/_/g, ' ');
};
const date = (item: string | null | undefined) =>
  item ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(item + 'T00:00:00')) : 'Not available';

const CollateralWorkspace: React.FC<Props> = props => {
  const [params, setParams] = useSearchParams();
  const mode = params.get('collateralView') === 'ingestion' ? 'ingestion' : 'explorer';
  const setMode = (next: 'explorer' | 'ingestion') => {
    const copy = new URLSearchParams(params);
    copy.set('collateralView', next);
    setParams(copy, { replace: false });
  };

  if (props.embedded) return <CollateralExplorer {...props} />;

  return (
    <div>
      <div style={{ ...panel, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '.65rem .75rem', marginTop: '.85rem', marginBottom: '.85rem' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={{ ...button, borderColor: mode === 'explorer' ? '#38bdf8' : 'rgba(148,163,184,.22)', background: mode === 'explorer' ? 'rgba(14,116,144,.2)' : button.background }} onClick={() => setMode('explorer')}>
            <FileSearch size={14} /> Collateral Explorer
          </button>
          <button type="button" style={{ ...button, borderColor: mode === 'ingestion' ? '#38bdf8' : 'rgba(148,163,184,.22)', background: mode === 'ingestion' ? 'rgba(14,116,144,.2)' : button.background }} onClick={() => setMode('ingestion')}>
            <Database size={14} /> Ingestion Center
          </button>
        </div>
        <div style={{ color: '#94a3b8', fontSize: '12px' }}>
          Delivery evidence and released collateral remain separate authorities.
        </div>
      </div>
      {mode === 'ingestion' ? (
        <IngestionWorkspace {...props} />
      ) : (
        <CollateralExplorer {...props} />
      )}
    </div>
  );
};

const CollateralExplorer: React.FC<Props> = ({ facilityId, initialTapeId, userRole, onFeedback }) => {
  const [params, setParams] = useSearchParams();
  const [model, setModel] = useState<CollateralExplorerReadModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get('poolSearch') || '');
  const [reason, setReason] = useState(params.get('reason') || '');
  // A stratification bucket (WS2) opens the register narrowed to its loans: ?poolDimension=STATE&poolBucket=TX.
  const [dimension, setDimension] = useState<CollateralDimension | undefined>((params.get('poolDimension') as CollateralDimension) || undefined);
  const [bucket, setBucket] = useState(params.get('poolBucket') || '');
  const [view, setView] = useState<CollateralView>((params.get('poolView') as CollateralView) || 'all');
  const [sortBy, setSortBy] = useState<CollateralSort>((params.get('sort') as CollateralSort) || 'balance');
  const [direction, setDirection] = useState<'asc' | 'desc'>((params.get('direction') as 'asc' | 'desc') || 'desc');
  const [tapeId, setTapeId] = useState<number | undefined>(() => {
    if (initialTapeId) return initialTapeId;
    const value = Number(params.get('tape'));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  });
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([]);
  const [detail, setDetail] = useState<CollateralAssetDetail | null>(null);
  useAbfCopilotContext({tapeId, selectedRowIds: detail ? [detail.asset.id] : undefined, subview: 'pool'});
  const [detailLoading, setDetailLoading] = useState(false);
  const [compareTapeId, setCompareTapeId] = useState<number | undefined>(() => {
    const value = Number(params.get('compareTape'));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  });
  const [comparison, setComparison] = useState<ReleasedTapeComparison | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [movementLoading, setMovementLoading] = useState(false);
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);
  const loadSequence = useRef(0);
  const detailSequence = useRef(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeDetail = useCallback(() => { ++detailSequence.current; setDetail(null); setDetailLoading(false); }, []);
  const detailOpen = detailLoading || Boolean(detail);
  useEffect(() => {
    if (detailOpen && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
  }, [detailOpen]);
  useEffect(() => () => { ++loadSequence.current; ++detailSequence.current; }, []);

  const updateUrl = useCallback((changes: Record<string, string | undefined>) => {
    const copy = new URLSearchParams(params);
    copy.set('collateralView', 'explorer');
    Object.entries(changes).forEach(([key, next]) => next ? copy.set(key, next) : copy.delete(key));
    setParams(copy, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async (notify = false) => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const next = await getCollateralExplorer(facilityId, {
        tapeIngestId: tapeId,
        view,
        search: search.trim() || undefined,
        reason: reason.trim() || undefined,
        dimension: dimension && bucket.trim() ? dimension : undefined,
        bucket: dimension && bucket.trim() ? bucket.trim() : undefined,
        sortBy,
        direction,
        cursor,
        limit: 50,
      });
      if (sequence !== loadSequence.current) return;
      setModel(next);
      setLocalFeedback(current => current?.tone === 'error' ? null : current);
      if (!tapeId && next.selectedTape?.id) {
        setTapeId(next.selectedTape.id);
        updateUrl({ tape: String(next.selectedTape.id) });
      }
      if (notify) {
        const feedback = { tone: 'success' as const, message: 'Released collateral, decisions, and lineage refreshed.' };
        setLocalFeedback(feedback);
        onFeedback(feedback);
      }
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Collateral Explorer could not be loaded.') };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [bucket, cursor, dimension, direction, facilityId, onFeedback, reason, search, sortBy, tapeId, updateUrl, view]);

  useEffect(() => { void load(false); }, [load]);

  const resetPage = () => {
    setCursor(undefined);
    setCursorHistory([]);
  };
  const selectView = (next: CollateralView) => {
    setView(next);
    resetPage();
    updateUrl({ poolView: next, reason: reason || undefined });
  };
  const openDetail = async (asset: CollateralAsset) => {
    if (!model?.selectedTape) return;
    const sequence = ++detailSequence.current;
    setDetail(null);
    setDetailLoading(true);
    try {
      const next = await getCollateralAssetDetail(facilityId, model.selectedTape.id, asset.id);
      if (sequence === detailSequence.current) setDetail(next);
    } catch (error) {
      if (sequence !== detailSequence.current) return;
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Loan evidence could not be loaded.') };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      if (sequence === detailSequence.current) setDetailLoading(false);
    }
  };
  const runComparison = useCallback(async (baseTapeId = compareTapeId) => {
    if (!baseTapeId || !model?.selectedTape || baseTapeId === model.selectedTape.id) return;
    setComparisonLoading(true);
    try {
      setComparison(await compareReleasedCollateralTapes(facilityId, baseTapeId, model.selectedTape.id));
      setCompareTapeId(baseTapeId);
      updateUrl({ compareTape: String(baseTapeId) });
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Released tapes could not be compared.') };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      setComparisonLoading(false);
    }
  }, [compareTapeId, facilityId, model?.selectedTape, onFeedback, updateUrl]);

  useEffect(() => {
    if (!compareTapeId || !model?.selectedTape || compareTapeId === model.selectedTape.id) return;
    if (comparison?.baseTape.id === compareTapeId
        && comparison.comparisonTape.id === model.selectedTape.id) return;
    void runComparison(compareTapeId);
  }, [compareTapeId, comparison, model?.selectedTape, runComparison]);

  const summary = model?.summary || {};
  const selectedTapeIndex = model?.tapes.findIndex(tape => tape.id === model.selectedTape?.id) ?? -1;
  const priorReleasedTape = selectedTapeIndex >= 0 ? model?.tapes[selectedTapeIndex + 1] : undefined;
  const normalizedRole = (userRole || '').toUpperCase();
  const canReconcileMovements = [
    'PM', 'PORTFOLIO_MANAGER', 'TRADER', 'RISK', 'RISK_MANAGER', 'OPERATIONS',
    'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN',
  ].includes(normalizedRole);
  const runMovementReconciliation = async () => {
    if (!model?.selectedTape || !priorReleasedTape || !canReconcileMovements) return;
    setMovementLoading(true);
    try {
      const run = await reconcileReleasedCollateralMovements(
        facilityId, model.selectedTape.id, priorReleasedTape.id,
      );
      const feedback = {
        tone: 'success' as const,
        message: plural(Number(run.material_count || 0), 'material movement') + ' reconciled against '
          + date(priorReleasedTape.as_of_date) + '.',
      };
      setLocalFeedback(feedback);
      onFeedback(feedback);
      await load(false);
    } catch (error) {
      const feedback = {
        tone: 'error' as const,
        message: apiErrorMessage(error, 'Collateral movements could not be reconciled.'),
      };
      setLocalFeedback(feedback);
      onFeedback(feedback);
    } finally {
      setMovementLoading(false);
    }
  };
  // Presentation only: which columns and captions the page needs.
  const pageItems = model?.page.items ?? [];
  const showIndustry = pageItems.some(asset => Boolean(asset.industry));
  const gridColumns = showIndustry ? LOAN_GRID_COLUMNS : LOAN_GRID_COLUMNS_NO_INDUSTRY;
  const hasDerivedTerm = pageItems.some(asset => asset.remaining_term_months != null && Boolean(asset.derived_fields?.includes('remaining_term_months')));
  const pageOffset = cursorHistory.length * Number(model?.page.limit || pageItems.length || 0);
  // The card counts are the whole snapshot; with a search, reason or bucket applied they are not
  // this list's total, so the caption then states only the page.
  const narrowed = Boolean(search.trim() || reason || bucket);
  const pageTotalRaw = narrowed ? null : view === 'all' ? summary.total_count : view === 'eligible' ? summary.eligible_count : view === 'ineligible' ? summary.ineligible_count : null;
  const pageTotal = pageTotalRaw == null ? null : Number(pageTotalRaw);
  const pageCaption = pageItems.length === 0 ? 'No records on this page'
    : pageTotal != null && Number.isFinite(pageTotal) && pageTotal >= pageOffset + pageItems.length
      ? `${(pageOffset + 1).toLocaleString('en-US')}–${(pageOffset + pageItems.length).toLocaleString('en-US')} of ${pageTotal.toLocaleString('en-US')}`
      : plural(pageItems.length, 'record') + ' on this page';

  const cards: Array<{ key: CollateralView; title: string; count: unknown; amount?: unknown; tone: string }> = [
    { key: 'all', title: 'All records', count: summary.total_count, amount: summary.total_balance, tone: '#38bdf8' },
    { key: 'eligible', title: 'Eligible', count: summary.eligible_count, amount: summary.eligible_balance, tone: '#34d399' },
    { key: 'ineligible', title: 'Ineligible', count: summary.ineligible_count, amount: summary.ineligible_balance, tone: '#fb7185' },
    { key: 'movements', title: 'Movements', count: summary.movement_count, tone: '#fbbf24' },
    { key: 'dq', title: 'Data quality', count: Number(summary.dq_rejected_count || 0) + Number(summary.quarantined_delivery_count || 0), tone: '#a78bfa' },
  ];
  if (loading && !model) return <ExplorerSurface><div className="empty-state" role="status"><Database size={30} /><h2>Loading collateral</h2><p>Retrieving released loans and their supporting evidence.</p></div></ExplorerSurface>;
  if (!model?.selectedTape) return <ExplorerSurface><div className="empty-state">
    <FileSearch size={32} color="#7dd3fc" /><h2>{localFeedback?.tone === 'error' ? 'Collateral could not be loaded' : 'Your released pool will appear here'}</h2>
    <p>{localFeedback?.tone === 'error' ? localFeedback.message : 'Receive a servicer tape in the Ingestion Center, review its mapping and data quality, then release it to explore loans and eligibility.'}</p>
    <button type="button" style={button} onClick={() => { const copy = new URLSearchParams(params); copy.set('collateralView','ingestion'); setParams(copy); }}><Database size={16} />Open Ingestion Center</button>
    {localFeedback?.tone === 'error' && <button type="button" style={{ ...button, marginLeft: 8 }} onClick={() => void load()}>Retry</button>}
  </div></ExplorerSurface>;

  return (
    <ExplorerSurface>
      <section style={{ ...panel, padding: 'clamp(16px,2vw,28px)' }}>
        <div className="eyebrow"><CheckCircle2 size={14} />Released collateral · {date(model.selectedTape.as_of_date)}</div>
        <div className="workspace-heading">
          <div>
            <h2>Collateral Explorer</h2>
            <p>
              Trace every pool total to released loans, source values, eligibility reasons, and contract rules.
            </p>
          </div>
          <button type="button" style={button} onClick={() => void load(true)} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="summary-grid" aria-label="Filter the collateral pool">
          {cards.map(card => (
            <button key={card.key} type="button" onClick={() => selectView(card.key)} aria-pressed={view === card.key} style={{
              minWidth: 0,
              padding: '18px 16px',
              textAlign: 'left',
              border: '1px solid ' + (view === card.key ? card.tone : 'rgba(148,163,184,.14)'),
              borderRadius: 11,
              background: view === card.key ? 'rgba(14,116,144,.13)' : 'rgba(15,23,42,.48)',
              cursor: 'pointer',
            }}>
              <div style={label}>{card.title}</div>
              <div style={{ marginTop: 10, fontSize: 'clamp(20px,1.7vw,28px)', fontWeight: 650, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.03em', color: card.tone }}>{card.amount != null ? money(card.amount) : number(card.count)}</div>
              <div style={{ color: '#a2b3c9', fontSize: '12px', marginTop: 4 }}>{card.amount != null ? `${number(card.count)} loans` : 'Recorded items'} · View details</div>
            </button>
          ))}
        </div>

        {view === 'movements' && (
          <div style={{ ...panel, marginTop: 12, padding: '.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ color: '#f8fafc', fontSize: '13px', fontWeight: 650 }}>Tape-over-tape movement register</div>
              <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: 3 }}>
                {priorReleasedTape
                  ? 'Compare ' + date(priorReleasedTape.as_of_date) + ' with ' + date(model.selectedTape.as_of_date) + ' and persist only material movement evidence.'
                  : 'A prior released tape is required before movements can be reconciled.'}
              </div>
            </div>
            <button
              type="button"
              style={{ ...button, opacity: priorReleasedTape && canReconcileMovements ? 1 : .45 }}
              disabled={!priorReleasedTape || !canReconcileMovements || movementLoading}
              onClick={() => void runMovementReconciliation()}
              title={!priorReleasedTape ? 'Release a second collateral tape first.' : !canReconcileMovements ? 'Your role can inspect movement evidence but cannot create a reconciliation run.' : undefined}
            >
              <GitCompareArrows size={14} /> {movementLoading ? 'Reconciling…' : Number(summary.movement_count || 0) > 0 ? 'Reconcile again' : 'Reconcile movements'}
            </button>
          </div>
        )}

        {model.tapes.length > 1 ? (
          <div style={{ ...panel, marginTop: 12, padding: '.8rem' }}>
            <div className="comparison-controls">
              <div>
                <div style={{ color: '#f8fafc', fontSize: '14px', fontWeight: 650 }}>Released tape comparison</div>
                <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: 3 }}>Bridge another released snapshot into the selected tape without changing either authority.</div>
              </div>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                <select aria-label="Reference tape for comparison" value={compareTapeId || ''} onChange={event => { const next = Number(event.target.value) || undefined; setCompareTapeId(next); setComparison(null); updateUrl({ compareTape: next ? String(next) : undefined }); }} style={{ minHeight: 40, padding: '.4rem .55rem', border: '1px solid rgba(148,163,184,.2)', borderRadius: 9, background: '#07101f', color: '#e2e8f0', fontSize: '12px' }}>
                  <option value="">Choose reference tape</option>
                  {model.tapes.filter(tape => tape.id !== model.selectedTape?.id).map(tape => <option key={tape.id} value={tape.id}>{date(tape.as_of_date)} · {display(tape.authority_status)}</option>)}
                </select>
                <button type="button" style={{ ...button, opacity: compareTapeId && compareTapeId !== model.selectedTape.id ? 1 : .45 }} disabled={!compareTapeId || compareTapeId === model.selectedTape.id || comparisonLoading} onClick={() => void runComparison()}>
                  <GitCompareArrows size={14} /> {comparisonLoading ? 'Comparing…' : 'Compare'}
                </button>
              </div>
            </div>
            {comparison && <TapeComparisonPanel comparison={comparison} />}
          </div>
        ) : (
          <div style={{ marginTop: 10, color: '#94a8c2', fontSize: '12px' }}>Tape comparison becomes available when this facility has two released collateral snapshots.</div>
        )}

        <div className="section-heading"><div><h3>Loan register</h3><p>{loading ? 'Updating results…' : pageCaption} · {cards.find(card => card.key === view)?.title}</p></div>
          {(search || reason || (dimension && bucket)) && <button type="button" style={button} onClick={() => { setSearch(''); setReason(''); setDimension(undefined); setBucket(''); resetPage(); updateUrl({ poolSearch: undefined, reason: undefined, poolDimension: undefined, poolBucket: undefined }); }}><X size={14} />Clear filters</button>}
        </div>
        {dimension && bucket && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 .6rem', color: '#cbd5e1', fontSize: '12px' }}>
            <span style={{ color: '#94a8c2' }}>Narrowed to</span>
            <span style={{ padding: '.2rem .55rem', border: '1px solid rgba(125,211,252,.35)', borderRadius: 999, background: 'rgba(125,211,252,.08)', color: '#7dd3fc', fontWeight: 650 }}>{display(dimension)} = {bucket}</span>
            <button type="button" aria-label="Remove dimension filter" style={{ ...button, padding: '.15rem .4rem' }} onClick={() => { setDimension(undefined); setBucket(''); resetPage(); updateUrl({ poolDimension: undefined, poolBucket: undefined }); }}><X size={12} /></button>
          </div>
        )}
        <div className="filter-bar">
          <label style={{ position: 'relative' }}><span>Find a loan</span>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 35, color: '#94a8c2' }} />
            <input aria-label="Search loans or obligors" value={search} onChange={event => { setSearch(event.target.value); resetPage(); updateUrl({ poolSearch: event.target.value || undefined }); }} placeholder="Search loan or obligor" style={{ width: '100%', minHeight: 36, boxSizing: 'border-box', padding: '.45rem .65rem .45rem 2rem', border: '1px solid rgba(148,163,184,.2)', borderRadius: 9, background: '#07101f', color: '#e2e8f0', fontSize: '13px' }} />
          </label>
          <label><span>Exclusion reason</span><input aria-label="Filter exclusion reason" value={reason} onChange={event => { setReason(event.target.value); resetPage(); updateUrl({ reason: event.target.value || undefined }); }} placeholder="Filter exclusion reason" style={{ minHeight: 36, padding: '.45rem .65rem', border: '1px solid rgba(148,163,184,.2)', borderRadius: 9, background: '#07101f', color: '#e2e8f0', fontSize: '13px' }} /></label>
          <label><span>Released snapshot</span><select aria-label="Released tape" value={model.selectedTape.id} onChange={event => { const next = Number(event.target.value); setTapeId(next); setComparison(null); if (next === compareTapeId) setCompareTapeId(undefined); resetPage(); updateUrl({ tape: event.target.value, compareTape: next === compareTapeId ? undefined : compareTapeId ? String(compareTapeId) : undefined }); }} style={{ minHeight: 36, padding: '.4rem .55rem', border: '1px solid rgba(148,163,184,.2)', borderRadius: 9, background: '#07101f', color: '#e2e8f0', fontSize: '13px' }}>
            {model.tapes.map(tape => <option key={tape.id} value={tape.id}>{date(tape.as_of_date)} · {display(tape.authority_status)}</option>)}
          </select></label>
          <label><span>Sort by</span><select aria-label="Sort loans by" value={sortBy} onChange={event => { const next = event.target.value as CollateralSort; setSortBy(next); resetPage(); updateUrl({ sort: next }); }} style={{ minHeight: 36, padding: '.4rem .55rem', border: '1px solid rgba(148,163,184,.2)', borderRadius: 9, background: '#07101f', color: '#e2e8f0', fontSize: '13px' }}>
            <option value="balance">Balance</option><option value="asset">Asset</option><option value="obligor">Obligor</option>
            <option value="dpd">Days past due</option><option value="fico">FICO</option><option value="ltv">LTV</option>
            <option value="vintage">Vintage</option><option value="geography">Geography</option>
            <option value="term">Remaining term</option><option value="rating">Rating</option><option value="industry">Industry</option>
          </select></label>
          <button type="button" style={button} onClick={() => { const next = direction === 'desc' ? 'asc' : 'desc'; setDirection(next); resetPage(); updateUrl({ direction: next }); }}>
            <SlidersHorizontal size={14} /> {direction === 'desc' ? 'High to low' : 'Low to high'}
          </button>
        </div>

        <div className="loan-table" role="region" tabIndex={0} aria-label="Released loan records" aria-busy={loading}>
          <div className="loan-table-header" style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 10, padding: '.65rem .7rem', background: '#101a2d', color: '#7dd3fc', fontSize: '12px', fontWeight: 650, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <div>Loan / obligor</div><div>Balance</div><div>Decision</div><div>Primary reason</div><div>DPD</div><div>Credit</div><div>Term</div>{showIndustry && <div>Industry</div>}<div>Source</div>
          </div>
          {view === 'dq' && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              Rejected rows do not enter released collateral. This selected tape has {number(summary.dq_rejected_count)} rejected rows; facility history contains {number(summary.quarantined_delivery_count)} quarantined deliveries.
            </div>
          )}
          {view !== 'dq' && model.page.items.map(asset => (
            <button className="loan-row" key={asset.id} type="button" disabled={loading} aria-label={`Open evidence for ${asset.asset_identifier}`} onClick={() => void openDetail(asset)} style={{ display: 'grid', width: '100%', gridTemplateColumns: gridColumns, gap: 10, alignItems: 'center', padding: '.7rem', border: 0, borderTop: '1px solid rgba(148,163,184,.11)', background: 'rgba(5,12,24,.35)', color: '#e2e8f0', textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ minWidth: 0 }}><div style={{ ...value, overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.asset_identifier}</div><div style={{ color: '#94a3b8', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.obligor_name || asset.obligor_identifier || 'Obligor not supplied'}</div></div>
              <div style={{ ...value, fontFamily: 'ui-monospace, monospace' }}>{money(asset.current_balance)}</div>
              <div style={{ color: asset.eligible ? '#6ee7b7' : '#fb7185', fontSize: '12px', fontWeight: 650 }}>{asset.eligible ? 'ELIGIBLE' : 'INELIGIBLE'}</div>
              <div style={{ color: asset.primary_reason ? '#fda4af' : '#94a3b8', fontSize: '12px', lineHeight: 1.35 }}>{view === 'movements' && asset.movement_type ? display(asset.movement_type) : asset.primary_reason || asset.movement_type || 'All applicable rules passed'}</div>
              <div style={value}>{asset.days_past_due}</div>
              <div style={{ color: '#cbd5e1', fontSize: '12px' }}>{asset.fico_score ? 'FICO ' + asset.fico_score : asset.ltv_pct !== null ? 'LTV ' + asset.ltv_pct + '%' : '—'}{(asset.credit_rating || asset.risk_grade) && <><br /><span style={{ color: '#94a3b8' }}>{[asset.credit_rating, asset.risk_grade].filter(Boolean).join(' · ')}</span></>}</div>
              <div style={{ color: '#cbd5e1', fontSize: '12px' }} title={asset.derived_fields?.includes('remaining_term_months') ? 'Remaining term derived from the maturity date' : undefined}>{termCell(asset)}</div>
              {showIndustry && <div style={{ color: '#cbd5e1', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.industry || '—'}</div>}
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>Row {asset.raw_row_number ?? '—'}<br />{asset.state || asset.geography || '—'}</div>
            </button>
          ))}
          {view !== 'dq' && model.page.items.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              {view === 'movements'
                ? priorReleasedTape
                  ? 'No persisted material movements match these filters. Reconcile the selected tape against its prior released snapshot.'
                  : 'Movement history becomes available after a second collateral tape is released.'
                : 'No released loans match these filters.'}
            </div>
          )}
        </div>

        {hasDerivedTerm && <div style={{ padding: '.5rem .7rem 0', color: '#94a3b8', fontSize: '12px' }}>* term derived from the maturity date</div>}
        <div className="pagination">
          <div style={{ color: '#94a3b8', fontSize: '12px' }}>{model.authorityBoundary}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={{ ...button, opacity: cursorHistory.length ? 1 : .45 }} disabled={!cursorHistory.length} onClick={() => { const previous = [...cursorHistory]; setCursor(previous.pop()); setCursorHistory(previous); }}><ArrowLeft size={14} /> Previous</button>
            <button type="button" style={{ ...button, opacity: model.page.hasMore ? 1 : .45 }} disabled={!model.page.hasMore || !model.page.nextCursor} onClick={() => { setCursorHistory([...cursorHistory, cursor]); setCursor(model.page.nextCursor || undefined); }}><ArrowRight size={14} /> Next 50</button>
          </div>
        </div>
        <ActionLocalFeedback feedback={localFeedback} />
      </section>

      <RuleWorkbench
        facilityId={facilityId}
        tapeIngestId={model.selectedTape.id}
        userRole={userRole}
        onFeedback={onFeedback}
      />

      {detailOpen && <LoanDialog ref={dialogRef} aria-label="Collateral loan evidence" onCancel={event => { event.preventDefault(); closeDetail(); }} onClick={event => { if (event.target === event.currentTarget) closeDetail(); }}>
        {detailLoading && !detail ? <div style={{ padding: 48, textAlign: 'center' }} role="status"><p>Loading loan evidence…</p><button type="button" style={button} onClick={closeDetail}>Cancel</button></div> : detail && <AssetDetail detail={detail} onClose={closeDetail} />}
      </LoanDialog>}
    </ExplorerSurface>
  );
};

const signedMoney = (amount: number) => {
  const rendered = money(Math.abs(amount));
  return amount > 0 ? '+' + rendered : amount < 0 ? '−' + rendered : rendered;
};
const TapeComparisonPanel: React.FC<{ comparison: ReleasedTapeComparison }> = ({ comparison }) => {
  const bridge = comparison.populationBridge;
  const metrics = comparison.metrics;
  const metricRows = [
    ['Total collateral', metrics.base_collateral_balance, metrics.comparison_collateral_balance, 'money'],
    ['Eligible collateral', metrics.base_eligible_balance, metrics.comparison_eligible_balance, 'money'],
    ['Ineligible collateral', metrics.base_ineligible_balance, metrics.comparison_ineligible_balance, 'money'],
    ['Computed borrowing base', metrics.base_computed_borrowing_base, metrics.comparison_computed_borrowing_base, 'money'],
    ['30+ delinquency', metrics.base_delinquency_30_pct, metrics.comparison_delinquency_30_pct, 'percent'],
    ['Default rate', metrics.base_default_rate_pct, metrics.comparison_default_rate_pct, 'percent'],
  ] as const;
  const metricValue = (amount: number | null, format: 'money' | 'percent') => amount === null ? 'Not assessed' : format === 'money' ? money(amount) : amount.toFixed(2) + '%';
  const metricDelta = (base: number | null, current: number | null, format: 'money' | 'percent') => {
    if (base === null || current === null) return 'Not comparable';
    const difference = current - base;
    return format === 'money' ? signedMoney(difference) : (difference > 0 ? '+' : '') + difference.toFixed(2) + ' pts';
  };
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid rgba(148,163,184,.12)', paddingTop: 12 }}>
      <div style={{ color: '#7dd3fc', fontSize: '12px', fontWeight: 650 }}>{date(comparison.baseTape.as_of_date)} <ArrowRight size={13} style={{ verticalAlign: 'middle', margin: '0 5px' }} /> {date(comparison.comparisonTape.as_of_date)}</div>
      <div className="detail-facts" style={{ marginTop: 12 }}>
        <Fact title="Opening pool" content={number(bridge.base_count) + ' · ' + money(bridge.base_balance)} />
        <Fact title="Added" content={'+' + number(bridge.added_count) + ' · ' + signedMoney(bridge.added_balance)} />
        <Fact title="Removed" content={'−' + number(bridge.removed_count) + ' · −' + money(bridge.removed_balance)} />
        <Fact title="Retained balance change" content={signedMoney(bridge.retained_balance_change)} />
        <Fact title="Closing pool" content={number(bridge.comparison_count) + ' · ' + money(bridge.comparison_balance)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginTop: 7 }}>
        <Fact title="Lost eligibility" content={number(bridge.eligibility_lost_count) + ' loans · ' + money(bridge.eligibility_lost_balance)} />
        <Fact title="Gained eligibility" content={number(bridge.eligibility_gained_count) + ' loans · ' + money(bridge.eligibility_gained_balance)} />
      </div>
      <div className="evidence-scroll" style={{ marginTop: 10, border: '1px solid rgba(148,163,184,.12)', borderRadius: 9 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr .8fr', gap: 8, padding: '.5rem .6rem', background: '#101a2d', color: '#7dd3fc', fontSize: '12px', fontWeight: 650, textTransform: 'uppercase' }}><div>Metric</div><div>Reference</div><div>Selected</div><div>Change</div></div>
        {metricRows.map(([name, base, current, format]) => <div key={name} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr .8fr', gap: 8, padding: '.5rem .6rem', borderTop: '1px solid rgba(148,163,184,.1)', color: '#cbd5e1', fontSize: '12px' }}><div>{name}</div><div>{metricValue(base, format)}</div><div>{metricValue(current, format)}</div><div>{metricDelta(base, current, format)}</div></div>)}
      </div>
      <div style={{ marginTop: 8, color: '#94a8c2', fontSize: '12px' }}>{comparison.authorityBoundary}</div>
    </div>
  );
};

const LOAN_GRID_COLUMNS = '1.45fr 1fr .7fr 1.3fr .5fr .75fr .8fr .9fr .65fr';
/** Consumer pools carry no industry; the column disappears rather than showing a page of dashes. */
const LOAN_GRID_COLUMNS_NO_INDUSTRY = '1.45fr 1fr .7fr 1.3fr .5fr .75fr .8fr .65fr';

const FREQUENCY_SHORT: Record<string, string> = { MONTHLY: 'mthly', QUARTERLY: 'qtrly', SEMI_ANNUAL: 'semi-ann', ANNUAL: 'annual', BULLET: 'bullet', IRREGULAR: 'irregular' };
const AMORTIZATION_SHORT: Record<string, string> = { LEVEL_PAY: 'level', INTEREST_ONLY: 'IO', BALLOON: 'balloon', BULLET: 'bullet', STRAIGHT_LINE: 'straight', REVOLVING: 'revolving' };

/** "38 mo · mthly · level", or a dash when the tape carried none of the three. */
const termCell = (asset: CollateralAsset) => {
  const parts = [
    asset.remaining_term_months == null ? null : `${asset.remaining_term_months} mo${asset.derived_fields?.includes('remaining_term_months') ? '*' : ''}`,
    asset.payment_frequency ? FREQUENCY_SHORT[asset.payment_frequency] || display(asset.payment_frequency) : null,
    asset.amortization_type ? AMORTIZATION_SHORT[asset.amortization_type] || display(asset.amortization_type) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
};

const Fact: React.FC<{ title: string; content: string }> = ({ title, content }) => (
  <div style={{ padding: '.55rem', border: '1px solid rgba(148,163,184,.12)', borderRadius: 8 }}>
    <div style={label}>{title}</div><div style={{ ...value, marginTop: 4 }}>{content}</div>
  </div>
);

const AssetDetail: React.FC<{ detail: CollateralAssetDetail; onClose: () => void }> = ({ detail, onClose }) => {
  const failed = detail.eligibility.trace.filter(step => step.decision === 'FAIL');
  const mappingBySource = new Map(detail.mapping.columns.map(column => [column.source_column, column]));
  // F-NS-012: detail.rawValues can be keyed by the CANONICAL field name, while the
  // mapping profile is keyed by the vendor source_column — so a join on source
  // alone showed "Profile mapping unavailable" for every column whose vendor and
  // canonical names differ (fico, dti, ltv, status, defaulted, borrower_name).
  // Resolve by either key.
  const mappingByCanonical = new Map(detail.mapping.columns.map(column => [column.canonical_field, column]));
  return (
    <>
      <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'space-between', gap: 12, padding: '1rem', borderBottom: '1px solid rgba(148,163,184,.16)', background: 'rgba(8,17,31,.97)' }}>
        <div>
          <div style={{ color: detail.asset.eligible ? '#6ee7b7' : '#fb7185', fontSize: '12px', fontWeight: 650 }}>{detail.eligibility.storedDecision}</div>
          <h2 style={{ margin: '.2rem 0', color: '#f8fafc', fontSize: '17px' }}>{detail.asset.asset_identifier}</h2>
          <div style={{ color: '#94a3b8', fontSize: '12px' }}>{detail.asset.obligor_name || detail.asset.obligor_identifier} · {money(detail.asset.current_balance)}</div>
        </div>
        <button type="button" autoFocus aria-label="Close loan evidence" style={button} onClick={onClose}><X size={15} /></button>
      </div>
      <div style={{ padding: '1rem' }}>
        <div className="detail-facts">
          <Fact title="Tape authority" content={display(detail.lineage.authority)} />
          <Fact title="Tape as of" content={date(detail.lineage.asOfDate)} />
          <Fact title="Source row" content={String(detail.lineage.sourceRow ?? 'Not available')} />
          <Fact title="Data quality" content={display(detail.lineage.dataQuality)} />
          <Fact title="Effective advance" content={detail.asset.effective_advance_rate_pct == null ? 'Not supplied' : `${detail.asset.effective_advance_rate_pct}%`} />
          <Fact title="Pledge" content={display(detail.pledge?.pledge_status || 'Not registered')} />
          <Fact title="Remaining term" content={detail.asset.remaining_term_months == null ? 'Not supplied' : `${detail.asset.remaining_term_months} months${detail.asset.derived_fields?.includes('remaining_term_months') ? ' (derived from maturity date)' : ''}`} />
          <Fact title="Payment terms" content={[detail.asset.payment_frequency, detail.asset.amortization_type].filter(Boolean).map(item => display(item)).join(' · ') || 'Not supplied'} />
          <Fact title="Rating / grade" content={[detail.asset.credit_rating, detail.asset.risk_grade].filter(Boolean).join(' · ') || 'Not supplied'} />
          <Fact title="Industry" content={detail.asset.industry || 'Not supplied'} />
        </div>

        <section className="evidence-scroll" style={{ ...panel, marginTop: 12, padding: '.8rem' }}>
          <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '14px' }}>{detail.asset.eligible ? 'Why included?' : 'Why excluded?'} <ExplainMetricButton scope="selection" metric="ELIGIBILITY" /></h3>
          <p style={{ margin: '.35rem 0 .7rem', color: '#94a3b8', fontSize: '12px' }}>
            The persisted decision {detail.eligibility.consistent ? 'matches' : 'does not match'} the exact effective-rule evaluator for this tape date.
          </p>
          {detail.eligibility.trace.map(step => (
            <div key={step.ruleId} style={{ display: 'grid', gridTemplateColumns: '.7fr 1.5fr .8fr .8fr 1.4fr', gap: 8, alignItems: 'center', padding: '.55rem 0', borderTop: '1px solid rgba(148,163,184,.11)' }}>
              <div style={{ color: step.decision === 'PASS' ? '#6ee7b7' : step.decision === 'FAIL' ? '#fb7185' : '#94a3b8', fontSize: '12px', fontWeight: 650 }}>{display(step.decision)}</div>
              <div style={value}>{step.businessName}</div>
              <div style={{ color: '#cbd5e1', fontSize: '12px' }}>{display(step.actualValue)}</div>
              <div style={{ color: '#cbd5e1', fontSize: '12px' }}>{display(step.operator)} {display(step.threshold)}</div>
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>{step.sourceClause || 'Legacy rule: no clause captured'}</div>
            </div>
          ))}
          {!failed.length && <div style={{ marginTop: 8, color: '#6ee7b7', fontSize: '12px' }}><CheckCircle2 size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />All applicable asset-eligibility rules passed.</div>}
        </section>

        <section className="evidence-scroll" style={{ ...panel, marginTop: 12, padding: '.8rem' }}>
          <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '14px' }}>Source to canonical comparison</h3>
          <p style={{ margin: '.35rem 0 .7rem', color: '#94a3b8', fontSize: '12px' }}>
            Raw vendor evidence is preserved beside normalized business values. Technical payload JSON is not the primary presentation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, padding: '.45rem .55rem', color: '#7dd3fc', fontSize: '12px', fontWeight: 650, textTransform: 'uppercase' }}>
            <div>Source field</div><div>Raw value</div><div>Canonical field</div><div>Canonical value / transform</div>
          </div>
          {Object.entries(detail.rawValues).map(([source, raw]) => {
            const mapping = mappingBySource.get(source) || mappingByCanonical.get(source);
            const canonical = mapping?.canonical_field;
            const sourceLabel = mapping?.source_column || source;
            return (
              <div key={source} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, padding: '.5rem .55rem', borderTop: '1px solid rgba(148,163,184,.1)', color: '#cbd5e1', fontSize: '12px' }}>
                <div>{display(sourceLabel)}</div><div>{display(raw)}</div><div>{canonical ? display(canonical) : 'Profile mapping unavailable'}</div>
                <div>{canonical ? display(detail.canonicalValues[canonical]) : '—'}{mapping?.transform_expression ? <div style={{ color: '#94a3b8' }}>{mapping.transform_expression}</div> : null}</div>
              </div>
            );
          })}
        </section>

        <div className="detail-grid" style={{ marginTop: 12 }}>
          <section style={{ ...panel, padding: '.8rem' }}>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '14px' }}>Movements</h3>
            {detail.movements.length ? detail.movements.map(movement => (
              <div key={movement.id} style={{ marginTop: 8, padding: '.65rem', border: '1px solid rgba(251,191,36,.24)', borderRadius: 8, background: 'rgba(251,191,36,.035)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: '#f8fafc', fontSize: '12px', fontWeight: 800 }}>
                  <span>{display(movement.movement_type)}</span>
                  <span style={{ color: '#94a3b8', fontWeight: 650 }}>{date(movement.prior_as_of_date)} → {date(movement.as_of_date)}</span>
                </div>
                <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center', color: '#cbd5e1', fontSize: '12px' }}>
                  <div><span style={{ color: '#94a8c2' }}>Before</span><br />{movement.prior_eligible == null ? 'Not available' : movement.prior_eligible ? 'Eligible' : 'Ineligible'} · {money(movement.prior_balance)}</div>
                  <ArrowRight size={13} color="#64748b" />
                  <div><span style={{ color: '#94a8c2' }}>After</span><br />{movement.current_eligible == null ? 'Not available' : movement.current_eligible ? 'Eligible' : 'Ineligible'} · {money(movement.current_balance)}</div>
                </div>
                <div style={{ marginTop: 6, color: '#94a3b8', fontSize: '12px' }}>{movement.message}</div>
              </div>
            )) : <p style={{ color: '#94a3b8', fontSize: '12px' }}>No material movement is recorded for this loan.</p>}
          </section>
          <section style={{ ...panel, padding: '.8rem' }}>
            <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '14px' }}>Pledge and fraud controls</h3>
            <p style={{ color: '#94a3b8', fontSize: '12px' }}>Pledge: {display(detail.pledge?.pledge_status || 'No registry record')}</p>
            <p style={{ color: detail.fraudFindings.length ? '#fda4af' : '#6ee7b7', fontSize: '12px' }}>{detail.fraudFindings.length ? plural(detail.fraudFindings.length, 'related finding') : 'No related fraud findings'}</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default CollateralWorkspace;
